import { useNavigate } from 'react-router-dom';
import { ModalType, ActionOpenModal } from '../core/modules/modals';
import { stringsRes } from '../resources/strings';
import { Ride, CancelRideOutput } from '../core/apiTypes';
import { useBreakpoints, WebLocation } from '../customHooks/useBreakpoints';
import { IconAxel } from '../resources/images';
import { ToastWithIcon } from '../navigation/routes/toastProvider';
import { ButtonType } from '../components/modals/templates/modalButton';
import { ModalSize } from '../components/modals/createModalHOC';
import useGlobalStyles from '../components/hook/useGlobalStyles';
import { clsx } from 'clsx';
import InfoBoxTex from '../components/infoBoxText';
import { getCancelReasonStatus, isBookingForLater } from '../utils/business';
import { makeStyles, createStyles } from '@mui/styles';
import CancelReservation from '../navigation/routes/reservations/cancelReservation';
import { useCancelRide, useDeleteRideRecurrent } from '../core/services/rides';
import FirebaseService from '../config/firebase';
import featureFlag from '../config/featureFlag';

interface UseCancellationProps {
    recurrent?: boolean;
    reservation: Ride;
    openModal: ActionOpenModal;
    onSuccess?: (data: any) => void; // Ajout du callback optionnel called on success and if the cancellation is free
}

export const useReservationCancellation = ({ reservation, recurrent, openModal, onSuccess }: UseCancellationProps) => {
    const navigate = useNavigate();
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);
    const globalClasses = useGlobalStyles();
    const classes = useStyles();

    // Définition des mutations directement dans le hook
    const cancelRide = useCancelRide();
    const deleteRide = useDeleteRideRecurrent();

    const handleCancelReservation = () => {
        if (
            recurrent ||
            isBookingForLater(reservation.status) ||
            !featureFlag.askCancelReason.active ||
            !getCancelReasonStatus().includes(reservation.status)
        ) {
            const modalProps = recurrent
                ? {
                      title: stringsRes.reservation.cancelRecurrent,
                      description: stringsRes.reservation.cancelRecurrentConfirm,
                      labelConfirm: stringsRes.common.cancel,
                      modalSize: ModalSize.sm,
                      customView: (
                          <InfoBoxTex description={stringsRes.recurrence.hint} className={classes.recurrenceInfoBox} />
                      ),
                  }
                : {
                      title: stringsRes.common.cancel,
                      description: stringsRes.booking.cancelRideConfirm,
                      labelConfirm: stringsRes.common.cancel,
                      modalSize: ModalSize.md,
                  };

            openModal(ModalType.CONFIRMATION, {
                ...modalProps,
                // Returning the promise keeps the modal open, confirm button loading, until the
                // cancellation really went through
                onConfirmAction: () =>
                    (recurrent ? deleteRide : cancelRide).mutateAsync({ id: reservation.id }).then((data) => {
                        // The reason flow below has its own success feedback (toast or fees modal),
                        // this shorter flow had none
                        ToastWithIcon(
                            'success',
                            recurrent ? stringsRes.booking.toast.canceledRecurrence : stringsRes.booking.cancelRideDone,
                            IconAxel.CarSide,
                        );
                        FirebaseService.logEvent('refund', data);
                        onSuccess?.(data);
                    }),
            });
        } else {
            openModal(ModalType.CONFIRMATION, {
                title: stringsRes.common.cancelRide,
                floatingClose: isMobileSize ? 'center' : 'none',
                ...(isMobileSize && { modalPosition: 'bottom' }),
                children: <CancelReservation />,
                modalSize: isMobileSize ? ModalSize.lg : ModalSize.xl,
                modalStyle: clsx(
                    globalClasses.modalRating,
                    //     isWebView() && classes.statusBarTop,
                    isMobileSize && globalClasses.modalBottomRating,
                ),
                ride: reservation,
                navigate,
                labelConfirm: stringsRes.common.cancel,
                onConfirmAction: (data: CancelRideOutput, reasonLabel: string) => {
                    if (data.rideUpdate?.price_details.cancellation_charge === 0) {
                        ToastWithIcon(
                            'success',
                            stringsRes.booking.noFees,
                            IconAxel.CarSide,
                            stringsRes.booking.cancelRideDone,
                        );
                        FirebaseService.logEvent('refund', data);
                        onSuccess?.(data);
                    } else {
                        openModal(ModalType.CONFIRMATION, {
                            title: stringsRes.booking.cancelRideWithCharge.title,
                            description: stringsRes.booking.cancelRideWithCharge.description,
                            floatingClose: isMobileSize ? 'center' : 'none',
                            ...(isMobileSize && { modalPosition: 'bottom' }),
                            customView: (
                                <InfoBoxTex
                                    titleIcon={'info'}
                                    title={`"${data?.core_res?.cancelledEntity?.cancel_reason ?? reasonLabel}"`}
                                    description={stringsRes.booking.cancelRideWithCharge.info}
                                    flexDirection="column"
                                />
                            ),
                            modalSize: isMobileSize ? ModalSize.lg : ModalSize.xl,
                            modalStyle: clsx(
                                globalClasses.modalRating,
                                //     isWebView() && classes.statusBarTop,
                                isMobileSize && globalClasses.modalBottomRating,
                            ),
                            typeConfirm: ButtonType.CONFIRM,
                            labelConfirm: stringsRes.common.gotIt,
                            noCancel: true,
                            onConfirmAction: () => {
                                onSuccess?.(data);
                            },
                        });
                    }
                },
            });
        }
    };

    return { handleCancelReservation };
};

const useStyles = makeStyles(() =>
    createStyles({
        recurrenceInfoBox: {
            marginTop: 8,
            flexDirection: 'row',
            columnGap: 12,
        },
    }),
);
